import React, { useEffect, useState } from 'react';
import { db } from '../firebase'; 
import { collection, collectionGroup, getDocs, query, where, Timestamp, doc, updateDoc, getDoc } from 'firebase/firestore';
import * as XLSX from 'xlsx';
import './UserManagement.css'; 

const Events = () => {
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('30d');
  const [activeJourneys, setActiveJourneys] = useState([]);
  const [completedJourneys, setCompletedJourneys] = useState([]);
  const [stats, setStats] = useState({ 
    triggers: 0, sharedTriggers: 0, emergencies: 0, sessions: 0, arrivals: 0, exitSurveys: 0 
  });

  useEffect(() => {
    fetchStats();
  }, [filter]);

  // ✅ FIXED: Reset logic using the dynamically resolved path
  const resetUnsafeCount = async (journey) => {
    if (!window.confirm(`Reset unsafe count for ${journey.userName}?`)) return;
    
    try {
      // Determine the correct reference based on the stored fullPath
      const journeyRef = doc(db, journey.fullPath);
      
      await updateDoc(journeyRef, { 
        unsafeCount: 0,
        updatedAtMs: Date.now() 
      });

      alert("Safety count reset successfully.");
      fetchStats(); 
    } catch (error) {
      console.error("Reset Error:", error);
      alert(`Reset failed: ${error.message}. Check console for path details.`);
    }
  };

  const fetchStats = async () => {
    setLoading(true);
    try {
      const now = new Date();
      let startTime = new Date();
      if (filter === '24h') startTime.setHours(now.getHours() - 24);
      if (filter === '7d') startTime.setDate(now.getDate() - 7);
      if (filter === '30d') startTime.setDate(now.getDate() - 30);
      if (filter === 'all') startTime = new Date('2020-01-01');

      const firestoreTimestamp = Timestamp.fromDate(startTime);
      const startTimeMs = startTime.getTime();

      const [trigSnap, sharedSnap, emergSnap, journeySnap, exitSnap] = await Promise.all([
        getDocs(query(collectionGroup(db, 'triggers'), where('createdAt', '>=', firestoreTimestamp))),
        getDocs(query(collectionGroup(db, 'sharedTriggers'), where('createdAt', '>=', firestoreTimestamp))),
        getDocs(query(collectionGroup(db, 'emergencies'), where('createdAt', '>=', firestoreTimestamp))),
        getDocs(query(collectionGroup(db, 'journeys'), where('updatedAtMs', '>=', startTimeMs))),
        // ✅ FETCHING EXIT SURVEYS
        getDocs(query(collection(db, 'exit_surveys'), where('timestamp', '>=', firestoreTimestamp)))
      ]);

      // --- 2. PROCESS JOURNEYS WITH DYNAMIC PATH RESOLUTION ---
      const processedJourneys = await Promise.all(journeySnap.docs.map(async (journeyDoc) => {
        const data = journeyDoc.data();
        const fullPath = journeyDoc.ref.path;
        const pathParts = fullPath.split('/');
        
        // RESOLVE USER ID:
        let userId = journeyDoc.id; 
        if (pathParts.includes('users')) {
          userId = pathParts[pathParts.indexOf('users') + 1];
        }

        // FETCH FULL NAME & EMAIL from Profiles
        let fullName = data.displayInitials || "User";
        let userEmail = data.userEmail || "N/A";

        try {
          const profileSnap = await getDoc(doc(db, "profiles", userId));
          if (profileSnap.exists()) {
            fullName = profileSnap.data().name || fullName;
            userEmail = profileSnap.data().email || userEmail;
          } else {
            const userSnap = await getDoc(doc(db, "users", userId));
            if (userSnap.exists()) {
              fullName = userSnap.data().name || fullName;
              userEmail = userSnap.data().email || userEmail;
            }
          }
        } catch (e) { console.warn("Name/Email lookup failed for", userId); }

        return {
          id: journeyDoc.id,
          uid: userId,
          fullPath: fullPath, 
          userName: fullName,
          userEmail: userEmail,
          destName: data.destName || 'N/A',
          unsafeCount: data.unsafeCount || 0,
          lastStatus: data.lastStatus || 'N/A',
          lastLocation: data.lastPromptLocName || data.lastStatusLocationName || 'Locating...',
          updatedAt: data.updatedAtMs ? new Date(data.updatedAtMs).toLocaleString() : 'N/A',
          isDanger: (data.unsafeCount || 0) >= 3 || data.lastStatus === 'UNSAFE',
          status: data.status,
          active: data.active
        };
      }));

      const active = processedJourneys.filter(j => j.active === true || j.status === 'ACTIVE');
      const completed = processedJourneys.filter(j => j.active === false);

      setActiveJourneys(active);
      setCompletedJourneys(completed);
      
      // ✅ STATS UPDATED WITH REAL DATA
      setStats({ 
        triggers: trigSnap.size, 
        sharedTriggers: sharedSnap.size, 
        emergencies: emergSnap.size, 
        sessions: processedJourneys.length,
        arrivals: completed.filter(j => j.status === 'ARRIVED').length,
        exitSurveys: exitSnap.size
      });

    } catch (error) { console.error("Fetch Error:", error); }
    setLoading(false);
  };

  const exportExcel = (data, name) => {
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Data");
    XLSX.writeFile(wb, `${name}_${new Date().toLocaleDateString()}.xlsx`);
  };

  if (loading) return <div className="loading-container"><h2>Refreshing Dashboard...</h2></div>;

  return (
    <div className="container" style={{padding: '20px'}}>
      <div className="header" style={{display:'flex', justifyContent:'space-between', alignItems:'center'}}>
        <h1>📈 System Analytics</h1>
        <div className="filters">
            <span style={{marginRight:'10px', fontWeight:'bold'}}>Time Range:</span>
            <select value={filter} onChange={(e) => setFilter(e.target.value)} className="type-select">
                <option value="24h">Last 24 Hours</option>
                <option value="7d">Last 7 Days</option>
                <option value="30d">Last 30 Days</option>
                <option value="all">All Time</option>
            </select>
        </div>
      </div>

      {/* ✅ STATS CARDS RESTORED */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '15px', margin: '20px 0' }}>
        <StatCard icon="🔔" title="Personal Triggers" count={stats.triggers} color="#4f46e5" />
        <StatCard icon="📤" title="Shared Triggers" count={stats.sharedTriggers} color="#0ea5e9" />
        <StatCard icon="🚨" title="Emergencies" count={stats.emergencies} color="#ef4444" />
        <StatCard icon="🚗" title="Live Journeys" count={activeJourneys.length} color="#8b5cf6" />
        <StatCard icon="🏁" title="Safe Arrivals" count={stats.arrivals} color="#10b981" />
        <StatCard icon="🚪" title="Exits" count={stats.exitSurveys} color="#64748b" />
      </div>

      <div className="table-section">
        <h2 style={{color: '#ef4444'}}>🚨 Live Monitoring</h2>
        <table className="admin-table">
          <thead>
            <tr><th>User Details</th><th>Email</th><th>Destination</th><th>Unsafes</th><th>Actions</th></tr>
          </thead>
          <tbody>
            {activeJourneys.map(j => (
              <tr key={j.id} className={j.isDanger ? 'row-danger' : ''}>
                <td>
                  <strong>{j.userName}</strong><br/>
                  <small style={{color: '#64748b'}}>{j.lastLocation}</small>
                </td>
                <td><small>{j.userEmail}</small></td>
                <td>{j.destName}</td>
                <td style={{textAlign:'center', fontWeight:'bold', color: j.unsafeCount > 0 ? 'red' : 'inherit'}}>{j.unsafeCount}</td>
                <td><button onClick={() => resetUnsafeCount(j)} className="reset-btn">Reset</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="table-section" style={{marginTop: '40px'}}>
        <h2 style={{color: '#10b981'}}>✅ History</h2>
        <table className="admin-table">
          <thead>
            <tr><th>User</th><th>Email</th><th>Outcome</th><th>Date</th></tr>
          </thead>
          <tbody>
            {completedJourneys.map(j => (
              <tr key={j.id}>
                <td><strong>{j.userName}</strong></td>
                <td><small>{j.userEmail}</small></td>
                <td>{j.status === 'ARRIVED' ? 'SAFE ARRIVAL' : j.status}</td>
                <td>{j.updatedAt}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const StatCard = ({ icon, title, count, color }) => (
  <div className="stat-card">
    <div style={{fontSize:'30px'}}>{icon}</div>
    <div style={{color:'#64748b', fontSize:'14px', margin:'5px 0'}}>{title}</div>
    <div style={{fontSize:'32px', fontWeight:'bold', color: color}}>{count}</div>
  </div>
);

export default Events;